import PropTypes from 'prop-types';
import React, { useMemo, useState, useContext, useCallback, useEffect } from 'react';
import _ from 'underscore';
import { useWizardService, matchStepState, STEP_STATES } from '@bingads-webui-react/wizard';
import { Stack, mergeStyles } from '@fluentui/react';
import { VideoAdsContainer, AdPreview } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { PerformanceMarkerContext } from '@bingads-webui-campaign-react/performance-marker-provider';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { useFormService } from '@bingads-webui-react/form';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { StepHeader } from '../../shared/step-header';
import { isCTVCampaign } from '../../step-constants';

const headerStyles = mergeStyles({
  width: 450,
});

const adPreviewStyles = mergeStyles({
  marginTop: 59,
});

export const VideoAds = ({
  data: ads,
  onDataChange,
  error,
  hideFooter,
  loadClipchampPlayer,
}) => {
  const {
    errors = [],
  } = error || {};

  const i18n = useContext(I18nContext);

  const activity = useContext(ActivityContext);

  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);

  const { scope } = useContext(ScopeContext) || {};
  const { cid, aid } = scope.values;

  const permissions = useContext(PermissionsContext);

  const perfMarker = useContext(PerformanceMarkerContext);

  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  const removeEmptyAds = newAdsData => newAdsData.filter(ad => !_.isEmpty(ad.data));

  const { state: { context: { saveFailed } } } = useFormService();

  const { state } = useWizardService();

  const {
    campaign = {}, editingAdId, flow, deal,
  } = state.context.wizardData;

  const { Id: campaignId } = campaign;

  const currentStates = state.toStrings()[1];

  const onAdsValidation = useCallback((results) => {
    const { data: newAdsData, ...restResults } = results;

    const newAds = removeEmptyAds(newAdsData);

    const dataPatch = {
      data: [
        ...newAds,
      ],
      ...restResults,
    };
    onDataChange({ data: dataPatch, isValid: dataPatch.isValid });
  }, [onDataChange]);

  useEffect(() => {
    if (activity && matchStepState(state, STEP_STATES.LOADING)) {
      activity.signal(`onVideoAdsStepLoading - ${currentStates}`);
    }

    if (activity && matchStepState(state, STEP_STATES.SAVING)) {
      activity.signal(`onVideoAdsStepSaving - ${currentStates}`);
    }
  }, [activity, currentStates, state]);

  const getBasicSettingsSection = useCallback(() => (
    <Stack horizontal token={{ childrenGap: 8 }} className="video-ads-basic-settings">
      <div>
        <div className={headerStyles}>
          <StepHeader
            title={editingAdId ? _TL_('Edit your video ads') : _TL_('Create your video ads')}
            description={_TL_('Provide the details and videos you want included in your ad.')}
          />
        </div>
        <div className="server-errors">
          <UnmappedFormErrors />
        </div>
        <VideoAdsContainer
          aid={aid}
          cid={cid}
          i18n={i18n}
          campaignId={campaignId}
          legacyI18n={legacyI18n}
          perfMarker={perfMarker}
          ads={ads.data}
          currentVideoIndex={currentVideoIndex}
          onAdsValidation={onAdsValidation}
          permissions={permissions}
          biddingScheme={{ Type: isCTVCampaign(flow) ? 'ManualCpv' : 'ManualCpm' }}
          allowCopilotVideoGeneration
          hideFooter={hideFooter}
          dealLoaded
          deal={deal}
          loadClipchampPlayer={loadClipchampPlayer}
        />
      </div>
      <div className={adPreviewStyles}>
        <AdPreview
          i18n={i18n}
          onUpdateCurrentVideoIndex={setCurrentVideoIndex}
          ads={ads}
          loadClipchampPlayer={loadClipchampPlayer}
          permissions={permissions}
        />
      </div>
    </Stack>
  ), [editingAdId, aid, cid, i18n, campaignId, legacyI18n, perfMarker, ads, currentVideoIndex, onAdsValidation, permissions, flow, hideFooter, deal]);

  return (
    <div className="video-ads-step">
      <ActuationGroup actuationProps={{ isActive: saveFailed }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors, showAllValidationErrors: saveFailed }}>
            {ads.data && getBasicSettingsSection()}
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

VideoAds.propTypes = {
  data: PropTypes.shape({
    data: PropTypes.shape({}),
  }),
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({
    errors: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  hideFooter: PropTypes.bool,
};

VideoAds.defaultProps = {
  data: {},
  error: {},
  hideFooter: false,
};
